import {useQuery, useQueryClient} from "@tanstack/react-query";
import {API, HTTPError} from "api";

function normalizeProductKey(product) {
  if (!product || product === "ALL") return "*";
  return product;
}

function makeKey(channel, product, type, set, layout, background, variant) {
  return [
    channel,
    normalizeProductKey(product),
    type,
    set || "",
    layout || "",
    background || "",
    // The brand-treatment axis, appended last so every key written before it
    // existed still reads back the same: a row with no variant keys on "".
    variant || "",
  ].join(":");
}

function buildLookup(overrides) {
  const lookup = new Map();
  for (const o of overrides || []) {
    const key = makeKey(o.channel, o.product, o.type, o.set, o.layout, o.background, o.variant);
    lookup.set(key, o);
  }
  return lookup;
}

/**
 * Resolve the most specific matching override entry.
 * Returns a full override object (not just scene_hash).
 */
export function resolveOverride(overrides, channel, product, type, set, layout, bg, variant) {
  if (!overrides?.length) return null;

  const lookup = buildLookup(overrides);
  const ch = channel || "";
  const t = type || "";
  const p = product || "";
  const s = set || "";
  const v = variant || "";

  const candidates = [
    // Per-product specific
    layout && v && makeKey(ch, p, t, s, layout, "", v),
    v && makeKey(ch, p, t, s, "", "", v),
    layout && bg && makeKey(ch, p, t, s, layout, bg, ""),
    layout && makeKey(ch, p, t, s, layout, "", ""),
    bg && makeKey(ch, p, t, s, "", bg, ""),
    makeKey(ch, p, t, s, "", "", ""),
    // Global (all products)
    layout && v && makeKey(ch, "*", t, s, layout, "", v),
    v && makeKey(ch, "*", t, s, "", "", v),
    layout && bg && makeKey(ch, "*", t, s, layout, bg, ""),
    layout && makeKey(ch, "*", t, s, layout, "", ""),
    bg && makeKey(ch, "*", t, s, "", bg, ""),
    makeKey(ch, "*", t, s, "", "", ""),
    // No set
    layout && makeKey(ch, p, t, "", layout, "", ""),
    bg && makeKey(ch, p, t, "", "", bg, ""),
    makeKey(ch, p, t, "", "", "", ""),
    layout && makeKey(ch, "*", t, "", layout, "", ""),
    bg && makeKey(ch, "*", t, "", "", bg, ""),
    makeKey(ch, "*", t, "", "", "", ""),
  ].filter(Boolean);

  for (const key of candidates) {
    const entry = lookup.get(key);
    if (entry) return entry;
  }

  return null;
}

/**
 * Resolve all matching overrides, ordered least -> most specific.
 * Used for cascading merge of partial scene payloads.
 */
export function resolveAllOverrides(overrides, channel, product, type, set, layout, bg, variant) {
  if (!overrides?.length) return [];

  const lookup = buildLookup(overrides);
  const ch = channel || "";
  const t = type || "";
  const p = product || "";
  const s = set || "";
  const v = variant || "";

  const candidates = [
    // Global (least specific)
    makeKey(ch, "*", t, "", "", "", ""),
    makeKey(ch, p, t, "", "", "", ""),
    makeKey(ch, "*", t, s, "", "", ""),
    bg && makeKey(ch, "*", t, s, "", bg, ""),
    layout && makeKey(ch, "*", t, s, layout, "", ""),
    // A (layout, background) row: what one layout changes about one
    // background, merged per template key on top of the background's own row.
    // Mirrored in resolve-defaults.ts.
    layout && bg && makeKey(ch, "*", t, s, layout, bg, ""),
    // The brand treatment: a row for the whole treatment, then the (layout,
    // variant) cell the design draws as one. Appended after the older layers
    // so nothing saved before the axis existed changes rank.
    v && makeKey(ch, "*", t, s, "", "", v),
    layout && v && makeKey(ch, "*", t, s, layout, "", v),
    // Per-product (most specific)
    makeKey(ch, p, t, s, "", "", ""),
    bg && makeKey(ch, p, t, s, "", bg, ""),
    layout && makeKey(ch, p, t, s, layout, "", ""),
    layout && bg && makeKey(ch, p, t, s, layout, bg, ""),
    v && makeKey(ch, p, t, s, "", "", v),
    layout && v && makeKey(ch, p, t, s, layout, "", v),
  ].filter(Boolean);

  const result = [];
  const seen = new Set();
  for (const key of candidates) {
    if (seen.has(key)) continue;
    seen.add(key);
    const entry = lookup.get(key);
    if (entry) result.push(entry);
  }

  return result;
}

/**
 * Pure derivation of the endpoint-status enum from React Query state.
 *
 * "no channel" treats endpoint as irrelevant; gate closed / still loading is
 * "pending" (consumers must not flash a warning before we've fetched);
 * "200 + unsupported: true" is a distinct failure mode from "404" — they
 * get different copy in the UI and shouldn't be collapsed into one boolean.
 * Non-404 errors (5xx, network) don't disable saving — likely transient.
 *
 * @returns {"available" | "unsupported" | "not-found" | "pending"}
 */
export function deriveEndpointStatus({hasChannel, isSuccess, data, isError, error}) {
  if (!hasChannel) return "available";
  if (isSuccess) return data?.unsupported === true ? "unsupported" : "available";
  if (isError)
    return error instanceof HTTPError && error.status === 404 ? "not-found" : "available";
  return "pending";
}

export function shouldRetryDefaultScenes(failureCount, error) {
  if (failureCount >= 2) return false;
  if (error instanceof HTTPError) return error.status >= 500;
  return true;
}

// One empty list for every render without data. A fresh `[]` each render made
// every memo that reads the list recompute each render: the ALL-products view
// rebuilt its default scene every frame and applied it again about 60 times a
// second, which undid the eyes and the banners an admin changed there.
const NO_OVERRIDES = Object.freeze([]);

/**
 * Fetches default scene overrides from backend via React Query.
 *
 * Pass product/type to fetch a server-filtered slice (the server keeps "*"
 * wildcard rows, so resolveOverride/resolveAllOverrides work unchanged on the
 * result). Omit them for the full channel list — needed by consumers that
 * resolve across many products at once (e.g. the all-products Whats-New page).
 */
export function useDefaultSceneOverrides(
  channel,
  {enabled: enabledGate = true, product = null, type = null} = {}
) {
  // enabledGate lets callers defer the fetch until they know the channel is
  // final — e.g. on /scene/:id routes, channel context defaults to the UI's
  // last-selected channel and gets corrected only after scene data arrives.
  // Fetching against the stale channel wastes a round trip.
  const hasChannel = Boolean(channel?.value);
  const enabled = hasChannel && enabledGate;
  const {data, isSuccess, isError, error, refetch} = useQuery({
    queryKey: ["default-scene-overrides", channel?.value, product, type],
    queryFn: () => API.getDefaultScenes(channel.value, {product, type}),
    enabled,
    refetchOnWindowFocus: false,
    retry: shouldRetryDefaultScenes,
    retryDelay: (attempt) => 500 * 2 ** attempt,
    staleTime: Infinity,
  });

  const endpointStatus = deriveEndpointStatus({hasChannel, isSuccess, data, isError, error});
  const isUnexpectedError = isError && !(error instanceof HTTPError && error.status === 404);

  return {
    overrides: data?.overrides ?? NO_OVERRIDES,
    // isResolved distinguishes "nothing to fetch" (no channel → trivially
    // resolved) from "deliberately held off" (gate closed → actively waiting).
    // Without that distinction, a gate-closed state would look identical to
    // "fetched, zero overrides found" to downstream consumers.
    isResolved: !hasChannel || (enabledGate && (isSuccess || isError)),
    endpointStatus,
    isEndpointAvailable: endpointStatus === "available",
    isError: isUnexpectedError,
    error: isUnexpectedError ? error : null,
    retry: refetch,
  };
}

/**
 * True when an override entry belongs in the cache slice identified by the
 * query key's product/type filters (null = unfiltered). Mirrors the server's
 * filter semantics: a product filter keeps "*" wildcard rows, type is exact.
 */
export function entryMatchesSlice(entry, productFilter, typeFilter) {
  if (productFilter && entry.product !== productFilter && entry.product !== "*") return false;
  if (typeFilter && entry.type !== typeFilter) return false;
  return true;
}

/**
 * Write an override into React Query cache — every cached slice of the
 * channel (channel-wide and each product/type-filtered variant) that the
 * entry belongs to, so filtered consumers see panel saves immediately.
 */
export function useSetLocalOverride() {
  const queryClient = useQueryClient();

  return ({
    channel,
    product,
    type,
    set,
    layout = null,
    background = null,
    variant = null,
    sceneData,
    sceneHash = null,
  }) => {
    const key = makeKey(channel, product, type, set, layout, background, variant);
    const entry = {
      channel,
      product,
      type,
      set,
      layout,
      background,
      variant,
      scene_data: sceneData,
      scene_hash: sceneHash,
    };

    const slices = queryClient.getQueriesData({queryKey: ["default-scene-overrides", channel]});
    for (const [queryKey, prev] of slices) {
      const [, , productFilter = null, typeFilter = null] = queryKey;
      if (!entryMatchesSlice(entry, productFilter, typeFilter)) continue;
      const overrides = prev?.overrides ? [...prev.overrides] : [];
      const idx = overrides.findIndex(
        (o) =>
          makeKey(o.channel, o.product, o.type, o.set, o.layout, o.background, o.variant) === key
      );
      if (idx >= 0) {
        overrides[idx] = entry;
      } else {
        overrides.push(entry);
      }
      queryClient.setQueryData(queryKey, {overrides});
    }
  };
}
